import React, { useMemo, useState } from 'react';
import { useData } from '../lib/DataContext.jsx';
import { SectionTitle, ScoreCell, DataBarCell, ProvenanceFooter, BandPill, ProvenanceBadge } from '../components/ui/kits.jsx';
import { ProjectCode } from '../components/ui/ProjectCode.jsx';
import { HBars, RingGauge, WeightDonut, Heatmap, Lollipop } from '../components/charts/charts.jsx';
import { RadarChart } from '../components/charts/advanced.jsx';
import { fmt, CRIT_ORDER, CRIT_COLOR, CRIT_ABBR, TOOL_COLOR } from '../lib/format.js';
import { Search, X } from 'lucide-react';

export default function OecdAnalysis() {
  const { data, agg: A, beneficiaries } = useData();
  const [query, setQuery] = useState('');

  // Map project code → canonical agency name for the ranked table
  const agencyByCode = useMemo(() => {
    if (!beneficiaries?.projects) return {};
    const m = {};
    for (const [code, b] of Object.entries(beneficiaries.projects)) {
      if (b?.researchAgency) m[code] = b.researchAgency;
    }
    return m;
  }, [beneficiaries]);

  const filteredProjects = useMemo(() => {
    if (!A) return [];
    const q = query.trim().toLowerCase();
    if (!q) return A.projectsSorted;
    return A.projectsSorted.filter((p) =>
      (p.code && p.code.toLowerCase().includes(q)) ||
      (p.partner && p.partner.toLowerCase().includes(q))
    );
  }, [A, query]);

  if (!data || !A) return null;
  const { meta, heatmap, weightSummary } = data;

  const heatRows = ['NRM', 'H&H', 'SDLE', 'PoE'];
  const heatKey = { NRM: 'NRM', 'H&H': 'Health', SDLE: 'SDLE', PoE: 'POE' };
  const cellsByRow = Object.fromEntries(heatmap.map((h) => [h.label, h.cells]));
  const overallByRow = Object.fromEntries(heatmap.map((h) => [h.label, h.overall]));

  return (
    <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }} data-testid="page-oecd">
      {/* Top row: weight donut + 4 ring gauges */}
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 14 }}>
        <div className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <SectionTitle right={<ProvenanceBadge kind="live" />}>Weight Distribution</SectionTitle>
          <WeightDonut weights={meta.weights} />
          <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            {CRIT_ORDER.map((c) => (
              <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: 'var(--ink-2)', fontWeight: 600 }}>
                <span style={{ width: 10, height: 10, background: CRIT_COLOR[c], borderRadius: 3 }} />
                {c} <span style={{ color: 'var(--ink-4)' }} className="tabular">{(meta.weights[c] * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={<ProvenanceBadge kind="live" />}>Thematic Tools · weighted overall</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
            {['NRM', 'Health', 'SDLE', 'POE'].map((t) => {
              const tile = A.tiles[t];
              return (
                <div key={t} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 6 }} data-testid={`ring-${t}`}>
                  <RingGauge score={tile.score} color={TOOL_COLOR[t]} />
                  <div style={{ marginTop: 4, fontWeight: 700, fontSize: 13 }}>{tile.label}</div>
                  <BandPill band={tile.band} />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Heatmap */}
      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <ProvenanceBadge kind="live" note="Rel · Eff · Sus computed from 638k responses" />
            <ProvenanceBadge kind="model" note="Coh · Effs · Imp · Brd — modelled placeholders" />
          </div>
        }>
          Tool × Criterion Heatmap
        </SectionTitle>
        <Heatmap
          rows={heatRows}
          cols={CRIT_ORDER}
          labels={CRIT_ABBR}
          valueAt={(row, col) => cellsByRow[row]?.[col]}
          overallByRow={overallByRow}
          overallLabel="Weighted"
        />
      </div>

      {/* Criterion averages */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14 }}>
        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={<ProvenanceBadge kind="live" />}>Criterion Averages · Programme</SectionTitle>
          <Lollipop
            testid="crit-avg-lollipop"
            rows={CRIT_ORDER.map((c) => ({
              label: c,
              value: A.critAvg[c],
              color: CRIT_COLOR[c],
              sub: `wt ${(meta.weights[c] * 100).toFixed(0)}%`,
            }))}
          />
        </div>

        <div className="card" style={{ padding: 18 }}>
          <SectionTitle right={
            <div style={{ display: 'flex', gap: 10, fontSize: 10.5, fontWeight: 600, alignItems: 'center' }}>
              <ProvenanceBadge kind="live" />
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#042a54' }}>
                <span style={{ width: 10, height: 10, background: '#042a54', borderRadius: 2 }} />HRDP
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#9b2d20' }}>
                <span style={{ width: 10, height: 10, background: '#9b2d20', borderRadius: 2 }} />FDP
              </div>
            </div>
          }>
            HRDP vs FDP · 7 criteria radar
          </SectionTitle>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <RadarChart
              axes={CRIT_ORDER.map((c) => ({ key: c, label: CRIT_ABBR[c] || c }))}
              series={[
                { name: 'HRDP', color: '#042a54', values: Object.fromEntries(CRIT_ORDER.map((c) => [c, A.hf[c]?.HRDP || 0])) },
                { name: 'FDP', color: '#9b2d20', values: Object.fromEntries(CRIT_ORDER.map((c) => [c, A.hf[c]?.FDP || 0])) },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Weighted contribution */}
      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={<ProvenanceBadge kind="live" note="Live except modelled criteria (Coh · Effs · Imp · Brd)" />}>Weighted Contribution Summary</SectionTitle>
          <table className="pv-table">
            <thead><tr><th>Criterion</th><th style={{ textAlign: 'right' }}>Weight</th><th style={{ textAlign: 'right' }}>Avg</th><th>Contribution</th></tr></thead>
            <tbody>
              {weightSummary.map((r) => (
                <tr key={r.criterion}>
                  <td style={{ color: CRIT_COLOR[r.criterion], fontWeight: 700 }}>{r.criterion}</td>
                  <td className="tabular" style={{ textAlign: 'right' }}>{(r.weight * 100).toFixed(0)}%</td>
                  <td className="tabular" style={{ textAlign: 'right' }}>{fmt(r.avg, 2)}</td>
                  <td><DataBarCell value={r.contribution} max={1} colorHint={CRIT_COLOR[r.criterion]} /></td>
                </tr>
              ))}
              <tr style={{ background: 'var(--surface-2)' }}>
                <td style={{ fontWeight: 800 }}>Total</td>
                <td className="tabular" style={{ textAlign: 'right', fontWeight: 800 }}>100%</td>
                <td />
                <td className="tabular" style={{ fontWeight: 800, color: 'var(--primary)' }}>{fmt(weightSummary.reduce((a, b) => a + b.contribution, 0), 2)} / 5.00</td>
              </tr>
            </tbody>
          </table>
      </div>

      {/* All projects ranked */}
      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ProvenanceBadge kind="live" />
            <div style={{ position: 'relative' }}>
              <Search size={13} color="var(--ink-4)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <input
                data-testid="project-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search project code or grantee…"
                style={{
                  padding: '6px 30px 6px 28px', border: '1px solid var(--line)',
                  borderRadius: 999, fontSize: 12, width: 240, outline: 'none',
                  background: 'var(--surface)', color: 'var(--ink)',
                  fontWeight: 500,
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--primary)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--line)'; }}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  data-testid="project-search-clear"
                  style={{
                    position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                    background: 'transparent', border: 0, cursor: 'pointer',
                    color: 'var(--ink-4)', padding: 3, display: 'flex',
                  }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
            <span style={{ fontSize: 11, color: 'var(--ink-4)' }} className="tabular" data-testid="project-search-count">
              {query
                ? `${filteredProjects.length} / ${A.projectsSorted.length} match`
                : `${A.projectsSorted.length} scored`
              }
            </span>
          </div>
        }>
          All Projects · Ranked
        </SectionTitle>
        <div style={{ marginTop: -4, marginBottom: 10, fontSize: 11.5, color: 'var(--ink-3)' }} data-testid="project-code-hint">
          Click on any project code to see beneficiary details.
        </div>
        <div className="scroll-y" style={{ maxHeight: 480 }}>
          <table className="pv-table">
            <thead>
              <tr>
                <th>#</th><th>Project</th><th>State</th><th>Type</th>
                <th className="hide-below-1200" style={{ minWidth: 140 }}>Agency</th>
                {CRIT_ORDER.map((c) => <th key={c} className="hide-below-1200" style={{ textAlign: 'center' }}>{CRIT_ABBR[c]}</th>)}
                <th>Overall</th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.length === 0 && (
                <tr><td colSpan={CRIT_ORDER.length + 6} style={{ textAlign: 'center', color: 'var(--ink-4)', padding: '30px 10px', fontStyle: 'italic' }}>
                  No project matches "{query}".
                </td></tr>
              )}
              {filteredProjects.slice(0, 60).map((p, i) => (
                <tr key={p.code}>
                  <td className="tabular" style={{ color: 'var(--ink-4)' }}>{i + 1}</td>
                  <td>
                    <div style={{ fontWeight: 700 }}><ProjectCode code={p.code} /></div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{p.partner || '—'}</div>
                  </td>
                  <td style={{ fontSize: 11.5 }}>{p.state || '—'}</td>
                  <td style={{ fontSize: 11, fontWeight: 700, color: p.type === 'HRDP' ? '#042a54' : '#9b2d20' }}>{p.type}</td>
                  <td className="hide-below-1200" style={{ fontSize: 11, color: 'var(--ink-2)' }} title={agencyByCode[p.code] || ''}>
                    {agencyByCode[p.code] || <span style={{ color: 'var(--ink-4)' }}>—</span>}
                  </td>
                  {CRIT_ORDER.map((c) => (
                    <td key={c} className="hide-below-1200" style={{ textAlign: 'center' }}>
                      <ScoreCell score={p.criteria?.[c]} />
                    </td>
                  ))}
                  <td><ScoreCell score={p.overall} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredProjects.length > 60 && (
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--ink-4)' }}>
            Showing first 60 of {filteredProjects.length} matches — narrow the search to see the rest.
          </div>
        )}
      </div>

      <ProvenanceFooter text={`Weight sum × avg = Σ contribution ≈ programme overall (${fmt(meta.programmeOverall, 2)}). Real criteria: ${meta.realCriteria.join(', ')}. Modelled: ${meta.derivedCriteria.join(', ')}.`} />
    </div>
  );
}
